import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'

import { useCurrentSurveyGroup } from '../../hooks/useCurrentSurveyGroup'
import {
  type IndicatorGroup,
  indicatorGroupLabelI18nKeyMap,
  indicatorLabelI18nKeyMap,
  indicatorMap,
} from '../../utils'

import { InPageLink, type InPageLinkProps } from './InPageLink'

export type LinkGroupProps = {
  indicatorGroup: IndicatorGroup
}

export const LinkGroup = ({ indicatorGroup }: LinkGroupProps) => {
  const surveyGroup = useCurrentSurveyGroup()
  const { t } = useTranslation()

  const propsArray = React.useMemo<InPageLinkProps[]>(
    () =>
      match(indicatorGroup)
        .with('fiveSegs', 'nineSegs', (indicatorGroup) =>
          indicatorMap[indicatorGroup].map<InPageLinkProps>((indicator) => ({
            indicator,
            label: t(indicatorLabelI18nKeyMap[indicator]),
          })),
        )
        .with('customized', () =>
          R.concat(
            surveyGroup.questions,
            surveyGroup.customerAttributeGroups,
          ).map<InPageLinkProps>(({ id, name }) => ({
            indicator: id,
            label: name,
          })),
        )
        .exhaustive(),
    [
      indicatorGroup,
      surveyGroup.customerAttributeGroups,
      surveyGroup.questions,
      t,
    ],
  )

  return (
    <VStack spacing={4}>
      <Typography variant="heading2">
        {t(indicatorGroupLabelI18nKeyMap[indicatorGroup])}
      </Typography>

      {propsArray.map(({ indicator, label }) => (
        <InPageLink key={indicator} indicator={indicator} label={label} />
      ))}
    </VStack>
  )
}
